/**
 * ShopDetails UI-only component.
 * All business logic is handled in useShopDetailsLogic.
 * Intended to be used via ShopDetailsWrapper HOC for isolation and composability.
 */
// import libraries
import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
} from "firebase/firestore";
import { firebase } from "../../../services/firebase";
import { logError } from "../../../utils/analytics";
import { Helmet } from "react-helmet";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
// import hooks
import { useShopDetailsLogic } from "./hooks/useShopDetailsLogic";
// import components
import ShopHeaderRow from "./components/ShopHeaderRow";
import LocationSection from "./components/LocationSection";
import BusinessHoursCard from "./components/BusinessHoursCard";
import ShopFeaturesSection from "./components/ShopFeaturesSection";
import FeatureContent from "../../../components/FeatureContent/FeatureContent";
// import constants
import { SHOP_TYPE_CATEGORIES } from "./constant";
import ArticleCard from "../../../components/ArticleCard/ArticleCard";
// import styling
import "./style.css";

const ShopDetails = ({ shopDetails: propShopDetails }) => {
  const { businessName } = useParams();
  const location = useLocation();
  const [shopDetails, setShopDetails] = useState(propShopDetails || null);
  const [shopFeatures, setShopFeatures] = useState({});
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(!propShopDetails);
  const [error, setError] = useState(null);

  const {
    setMap,
    directionsError,
    setDirectionsError,
    requestingDirections,
    routePath,
    setRoutePath,
    travelMode,
    setTravelMode,
    center,
  } = useShopDetailsLogic(shopDetails);

  const fetchShopFeatures = async (uid) => {
    try {
      const userDoc = await getDoc(doc(firebase, "users", uid));
      if (!userDoc.exists()) {
        console.log('User document not found for uid:', uid);
        return {};
      }

      const userData = userDoc.data();
      
      // If shopType is an array, it's the old format, return it directly
      if (Array.isArray(userData?.shopType)) {
        return { 'Shop Types': userData.shopType };
      }

      // If shopType is an object, process it
      if (userData.shopType && typeof userData.shopType === 'object') {
        const formattedFeatures = {};
        
        Object.entries(userData.shopType).forEach(([category, items]) => {
          if (!Array.isArray(items)) return;
          
          const formattedItems = items.map((item) => {
            if (!item || typeof item !== 'object') return null;
            
            // Handle both string and object items
            const itemData = typeof item === 'string' ? { type: item } : { ...item };
            
            // Extract subcategory from type if it contains an underscore
            if (itemData.type && typeof itemData.type === 'string') {
              const subCategory = itemData.type.includes('_')
                ? itemData.type.split('_').slice(1).join('_')
                : '';
              
              return {
                ...itemData,
                subCategory: subCategory.charAt(0).toUpperCase() + subCategory.slice(1)
              };
            }
            
            return itemData;
          }).filter(Boolean); // Remove any null/undefined items

          if (formattedItems.length > 0) {
            formattedFeatures[category] = formattedItems;
          }
        });

        console.log('Formatted features:', formattedFeatures);
        return formattedFeatures;
      }

      console.log('No valid shopType data found');
      return {};
    } catch (error) {
      logError("fetchShopFeatures", error);
      return {};
    }
  };

  const fetchArticles = async (shopId) => {
    try {
      const articlesRef = collection(firebase, "articles");
      const q = query(articlesRef, where("authorId", "==", shopId));
      const articlesSnapshot = await getDocs(q);

      const articlesData = articlesSnapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .filter((article) => article.status === "PUBLISHED");

      return articlesData;
    } catch (error) {
      logError("fetchArticles", error);
      return [];
    }
  };

  useEffect(() => {
    // If we already have shopDetails from props, skip fetching
    if (propShopDetails) {
      setShopDetails(propShopDetails);
      // Still need to fetch features and articles
      const fetchAdditionalData = async () => {
        const [features, shopArticles] = await Promise.all([
          fetchShopFeatures(propShopDetails.uid || propShopDetails.id),
          fetchArticles(propShopDetails.uid || propShopDetails.id),
        ]);
        setShopFeatures(features);
        setArticles(shopArticles);
        setLoading(false);
      };
      fetchAdditionalData();
      return;
    }

    const fetchBusinessData = async () => {
      if (!businessName) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Query Firestore for business with this name
        const usersRef = collection(firebase, "users");
        const q = query(usersRef, where("shopName", "==", businessName));
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          // Try a more flexible search if no exact match
          const allUsersQuery = query(usersRef);
          const allUsers = await getDocs(allUsersQuery);

          const matchingUsers = allUsers.docs.filter((doc) => {
            const data = doc.data();
            return (
              data.shopName &&
              data.shopName.toLowerCase().includes(businessName.toLowerCase())
            );
          });

          if (matchingUsers.length > 0) {
            await processShopData(matchingUsers[0].id, matchingUsers[0].data());
          } else {
            setError("Business not found");
          }
        } else {
          // Exact match found
          const doc = querySnapshot.docs[0];
          await processShopData(doc.id, doc.data());
        }
      } catch (error) {
        logError("ShopDetails", error);
        setError("Error loading business data");
      } finally {
        setLoading(false);
      }
    };

    const processShopData = async (shopId, userData) => {
      try {
        const shopData = {
          id: shopId,
          ...userData,
          shopName: userData.shopName || businessName,
          storeDescription:
            userData.storeDescription ||
            `${userData.shopName || businessName} - Connect via Yes Boss`,
          markerPosition: userData.markerPosition,
        };

        // Use either uid or id from userData, fallback to shopId
        const userId = userData.uid || userData.id || shopId;

        // Fetch shop features and articles in parallel
        const [features, shopArticles] = await Promise.all([
          fetchShopFeatures(userId),
          fetchArticles(userId),
        ]);
        setShopFeatures(features);
        setArticles(shopArticles);
        setShopDetails(shopData);
      } catch (error) {
        logError("processShopData", error);
        throw error;
      }
    };


    if (!propShopDetails) {
      fetchBusinessData();
    }
  }, [businessName, propShopDetails]);

  if (loading) {
    return (
      <div
        className="flex align-items-center justify-content-center"
        style={{ height: "50vh" }}
      >
        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
        <span className="ml-2">Loading business profile...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-column align-items-center justify-content-center p-5">
        <i
          className="pi pi-exclamation-triangle"
          style={{ fontSize: "3rem", color: "var(--yellow-500)" }}
        ></i>
        <h2>Business Not Found</h2>
        <p className="mb-4">
          We couldn't find a business with the name "{businessName}"
        </p>
      </div>
    );
  }

  if (!shopDetails) {
    return null;
  }

  const toast = React.createRef(null);

  const getSectionIdFromLabel = (label) => {
    return label
      .replace(/^[\W]+/, "") // Remove leading emoji or symbol
      .replace(/[^a-zA-Z0-9]+/g, "-") // Replace non-alphanumeric with dash
      .replace(/-+/g, "-") // Collapse multiple dashes
      .replace(/^-|-$/g, "") // Remove leading/trailing dash
      .toLowerCase();
  };

  return (
    <>
      <Helmet>
        <title>{shopDetails.shopName} | Yes Boss</title>
        <meta name="description" content={shopDetails.storeDescription} />
        <meta
          property="og:title"
          content={`${shopDetails.shopName} | Yes Boss`}
        />
        <meta
          property="og:description"
          content={shopDetails.storeDescription}
        />
        <meta
          property="og:image"
          content={
            shopDetails.storeImage || `${window.location.origin}/logo.png`
          }
        />
        <meta property="og:url" content={window.location.href} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>

      <div className="business-profile-portrait">
        <ShopHeaderRow
          shop={shopDetails}
          SHOP_TYPE_CATEGORIES={SHOP_TYPE_CATEGORIES}
        />

        <Divider />

        <LocationSection
          shop={shopDetails}
          center={center}
          setMap={setMap}
          travelMode={travelMode}
          setDirectionsError={setDirectionsError}
          setRoutePath={setRoutePath}
          setTravelMode={setTravelMode}
          requestingDirections={requestingDirections}
          routePath={routePath}
          directionsError={directionsError}
        />

        {shopDetails.businessHours?.length > 0 && (
          <>
            <Divider />
            <div className="business-hours-center">
              <BusinessHoursCard businessHours={shopDetails.businessHours} />
            </div>
          </>
        )}
        {/* {shopDetails.shopType && shopDetails.shopType.length > 0 && (
          <>
            <Divider />
            <div className="info-section shop-features-section">
              {(() => {
                const featureEntries = Object.entries(shopFeatures);
                
                // If there's only one feature and it has items, show the detailed view
                if (featureEntries.length === 1) {
                  const [category, items] = featureEntries[0];
                  const featureItems = Array.isArray(items) ? items : [];
                  
                  if (featureItems.length > 0) {
                    return (
                      <div style={{ width: '100%' }} className="services-section">
                        <h3 className="section-title">
                          <i className="pi pi-info-circle mr-2"></i>
                          Services and Information
                        </h3>
                        {featureItems.map((item, index) => (
                          <FeatureContent 
                            key={index}
                            featureDetails={{
                              ...(typeof item === 'string' ? { type: item } : item),
                              category,
                              businessHours: shopDetails.businessHours,
                              // Include shop details that might be needed
                              ...(shopDetails.phoneNumber && { phoneNumber: shopDetails.phoneNumber }),
                              ...(shopDetails.email && { email: shopDetails.email }),
                              ...(shopDetails.website && { website: shopDetails.website }),
                            }} 
                          />
                        ))}
                      </div>
                    );
                  }
                }
                
                // Otherwise, show the regular shop features section
                return featureEntries.map(([category, items]) => {
                  const sectionId = getSectionIdFromLabel(category);
                  const formattedItems = Array.isArray(items) 
                    ? items.map(item => ({
                        type: item,
                        subCategory: item.includes('_') 
                          ? item.split('_').slice(1).join('_')
                              .split('_')
                              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                              .join(' ')
                          : item
                      }))
                    : items;

                  return (
                    <div
                      id={sectionId}
                      key={category}
                      style={{ scrollMarginTop: 100 }}
                    >
                      <ShopFeaturesSection
                        shopId={shopDetails.id}
                        category={category}
                        items={formattedItems}
                        shopTypeCategories={SHOP_TYPE_CATEGORIES}
                        shopDetails={shopDetails}
                        articles={articles}
                      />
                    </div>
                  );
                });
              })()}
            </div>
          </>
        )} */}

        {/* Articles Section */}
        {articles.length > 0 && (
          <>
            <Divider />
            <div className="info-section articles-section">
              <h3 className="section-title">
                <i className="pi pi-book mr-2"></i>
                Latest Articles
              </h3>
              <div className="articles-grid">
                {articles.map((article) => (
                  <ArticleCard 
                    key={article.id} 
                    article={{
                      ...article,
                      // Map the data to match the expected format
                      thumbnail: article.thumbnail ? { 
                        url: article.thumbnail.url,
                        alt: article.thumbnail.alt
                      } : null,
                      summary: article.introduction
                    }}
                    className="article-card-item"
                  />
                ))}
              </div>
            </div>
          </>
        )}

        <Toast ref={toast} />
      </div>
    </>
  );
};

ShopDetails.displayName = "ShopDetails";
export default ShopDetails;
